<template>
  <div
    class="page-level-one"
    :class="{ info: isInfoPage, catalogue: isCataloguePage, noSpaceX: isNoSpaceX }"
  >
    <InfoPage v-if="isInfoPage" :current-info-page="pageData" :page-id="pageId" />
    <CataloguePage
      v-if="isCataloguePage"
      :page-type="pageType"
      :current-info-page="pageData"
      :page-id="pageId"
    />
  </div>
</template>

<script lang="ts" setup>
import { computed, watch } from 'vue';
import CataloguePage from '~/components/_views/cataloguePage.vue';
import InfoPage from '~/components/_views/infoPage.vue';
import { useStoreModals } from '~/stores/abstract/modals';
import { useStorePages } from '~/stores/abstract/pages';
import { useStoreRoot } from '~/stores/abstract/root';
import { useStoreCataloguePage } from '~/stores/strapi/cataloguePage';
import { useStoreInfoPages } from '~/stores/strapi/infoPages';
import { EPageType } from '~/types/pageType';
import { fetchPage } from '~/utils/fetchPage';
import type { IMirrorSeoHeadComponent } from '~/types/seoHead';

const context = useNuxtApp();
const route = useRoute();
const storeInfoPages = useStoreInfoPages();
const storePages = useStorePages();
const storeRoot = useStoreRoot();
const storeModals = useStoreModals();

const isNoSpaceX = computed(() => pageId.value === 'wheel-of-fortune');

/**
 * trying to find path into info pages
 */
const isInfoPage = computed((): boolean => {
  return storeInfoPages.pageType === EPageType.info;
});

/**
 * if info pages loaded and pageId is not info page - open catalogue
 */
const isCataloguePage = computed((): boolean => {
  return (
    storeInfoPages.pageType === EPageType.provider ||
    storeInfoPages.pageType === EPageType.category ||
    storeInfoPages.pageType === EPageType.slotTheme ||
    !pageId.value
  );
});

/**
 * getter for pageId property from $route
 */
const pageId = computed(() => {
  let _pageId = (route.params.pageId as string) || (route.meta.pageId as string) || 'allGames';

  if (_pageId === 'allGames' && route.path.startsWith('/slot-themes')) {
    _pageId = 'slot-themes';
  }

  return _pageId;
});

const pageType = computed(() => storeInfoPages.pageType);
const pageData = computed(() => storeInfoPages.pageData);

const { data, status } = await useAsyncData(
  `pageTypeData_${pageId.value}`,
  async () => {
    storeRoot.serverInit();

    context._route = route;
    const [pageTypeData] = await Promise.all([
      fetchPage(context),
      !storePages.genarationDateMain && storePages.loadMainServerData(),
    ]);

    return pageTypeData;
  },
  { deep: false, server: false }
);

const { headGenerate, getMirrorCanonical } = useHeadGenerate();

if (data.value?.type === EPageType.info) {
  const geo = useStoreRoot().geo;
  const item = JSON.parse(JSON.stringify(data.value?.data));
  if (geo && geo !== 'main') {
    const mirrorHead = data.value?.data.mirrorHead?.find(
      (head: IMirrorSeoHeadComponent) => head?.geo === geo
    );
    if (mirrorHead) {
      item.head.title = mirrorHead.title;
      item.head.description = mirrorHead.description || '';
      item.head.nofollow = mirrorHead.nofollow || false;
      item.head.noindex = mirrorHead.noindex || false;
      item.head.canonical = getMirrorCanonical(mirrorHead);
    }
  }
  useHead(() => headGenerate('', item.head));
} else if (
  [EPageType.category, EPageType.provider, EPageType.slotTheme].includes(data.value?.type as any)
) {
  useHead(() => headGenerate('', data.value?.data.head, useStoreCataloguePage()));
}

if (!data.value && status.value === 'success') {
  await navigateTo(storeRoot.localePath('/not-found'), { redirectCode: 404 });
}

watch(
  () => route.query.showLoginModal,
  (showModal) => {
    if (showModal === 'true') {
      storeModals.openLoginOrSignupModal();
      const router = useRouter();
      router.replace({ query: {} });
    }
  },
  { immediate: true }
);
</script>
